// apps/curbpass/src/routes/vehicles/VehiclesPage.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_CURRENTLY_REGISTERED_VEHICLES,
  GetCurrentlyRegisteredVehiclesData,
  GetCurrentlyRegisteredVehiclesVars,
  REMOVE_REGISTERED_VEHICLE,
  RemoveRegisteredVehicleVars,
} from "common/graphql";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import { UPDATE_REGISTERED_VEHICLE, UpdateRegisteredVehicleData, UpdateRegisteredVehicleVars } from "common/graphql";
import { LicensePlateState, StateAbbreviation, VehicleTypeValue } from "@app.automotus.io/components/common";
import { EditBuffer, NewVehicleRow } from "./vehicles.types";
import { validateVehicleRow } from "./validateVehicleRow";
import { useBulkSubmit, BulkSubmitResult } from "./hooks/useBulkSubmit";
import REGISTER_VEHICLE, { RegisterVehicleData, RegisterVehicleVars } from "common/graphql/mutations/RegisterVehicle";
import { VehiclesPageContent } from "./VehiclesPageContent";

export const VehiclesPage: React.FC = () => {
  const { userProfile } = useUserProfile();
  const isFleetOperator = userProfile?.account.ownerType === "fleet_operator";
  const defaultCategory = isFleetOperator ? "commercial" : "personal";

  const [newRows, setNewRows] = useState<NewVehicleRow[]>([]);
  const [bannerResult, setBannerResult] = useState<BulkSubmitResult | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const {
    data,
    loading: vehiclesLoading,
    refetch,
  } = useQuery<GetCurrentlyRegisteredVehiclesData, GetCurrentlyRegisteredVehiclesVars>(
    GET_CURRENTLY_REGISTERED_VEHICLES,
    {
      variables: {
        limit: pageSize,
        offset: page * pageSize,
        search: searchQuery ? `%${searchQuery.replace(/[%_\\]/g, "\\$&")}%` : "%",
      },
      fetchPolicy: "no-cache",
    },
  );

  const totalCount = data?.registeredVehicleCount?.aggregate?.count ?? 0;
  const existingVehicles = useMemo(() => data?.registeredVehicles ?? [], [data?.registeredVehicles]);

  const handleSearchQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  const [removeVehicle] = useMutation<unknown, RemoveRegisteredVehicleVars>(REMOVE_REGISTERED_VEHICLE);

  const [registerVehicle] = useMutation<RegisterVehicleData, RegisterVehicleVars>(REGISTER_VEHICLE);

  const [updateVehicle] = useMutation<UpdateRegisteredVehicleData, UpdateRegisteredVehicleVars>(
    UPDATE_REGISTERED_VEHICLE,
  );

  const handleDeleteExisting = useCallback(
    async (ids: string[]) => {
      await Promise.all(ids.map((id) => removeVehicle({ variables: { id } })));
      await refetch();
    },
    [removeVehicle, refetch],
  );

  const handleSaveEdit = useCallback(
    async (id: string, editData: EditBuffer) => {
      await updateVehicle({
        variables: {
          id,
          licensePlate: editData.licensePlate,
          stateCode: editData.stateCode as StateAbbreviation,
          type: (editData.type as VehicleTypeValue) || null,
          make: editData.make || null,
          model: editData.model || null,
        },
      });
      await refetch();
    },
    [updateVehicle, refetch],
  );

  const handleSubmitNew = useCallback(
    async (data: Pick<NewVehicleRow, "stateCode" | "licensePlate" | "make" | "model" | "type" | "category">) => {
      const countryCode =
        LicensePlateState.FromAbbreviation(data.stateCode as StateAbbreviation)?.countryCode === "CA" ? "CAN" : "USA";
      await registerVehicle({
        variables: {
          licensePlate: data.licensePlate,
          stateCode: data.stateCode as StateAbbreviation,
          countryCode,
          type: data.type as VehicleTypeValue,
          category: data.category,
          make: data.make || null,
          model: data.model || null,
        },
      });
      await refetch();
      setPage(0);
    },
    [registerVehicle, refetch],
  );

  const handleRowsParsed = useCallback(
    (parsed: NewVehicleRow[]) => {
      const withDefault = parsed.map((row) =>
        row.category ? row : { ...row, category: defaultCategory as NewVehicleRow["category"] },
      );
      setNewRows((prev) => {
        const combined = [...prev, ...withDefault];
        return combined.map((row) => {
          const others = combined.filter((r) => r.id !== row.id);
          const { errors } = validateVehicleRow(row, others, existingVehicles);
          return { ...row, errors };
        });
      });
    },
    [existingVehicles, defaultCategory],
  );

  const handleComplete = useCallback(
    (result: BulkSubmitResult) => {
      setBannerResult(result);
      if (result.networkError) {
        refetch();
        return;
      }
      if (page === 0) {
        refetch();
      }
      setPage(0);
      if (result.failedRows.length > 0) {
        const revalidated = result.failedRows.map((row, i) => ({
          ...row,
          id: `failed-${Date.now()}-${Math.random().toString(36).slice(2)}-${i}`,
        }));
        setNewRows(revalidated);
      } else {
        setNewRows([]);
      }
    },
    [refetch, page],
  );

  const { submit, status, progress } = useBulkSubmit({ onComplete: handleComplete });

  const hasValidationErrors = newRows.some((r) => Object.keys(r.errors).length > 0 || !!r.submitErrorCode);
  const canSubmit = newRows.length > 0 && !hasValidationErrors && status !== "submitting";
  const isSubmitting = status === "submitting";

  const handleNewRowsChange = useCallback(
    (rows: NewVehicleRow[]) => {
      const withDefault = rows.map((row) =>
        row.category ? row : { ...row, category: defaultCategory as NewVehicleRow["category"] },
      );
      const revalidated = withDefault.map((row) => {
        const others = withDefault.filter((r) => r.id !== row.id);
        const { errors } = validateVehicleRow(row, others, existingVehicles);
        return { ...row, errors };
      });
      setNewRows(revalidated);
    },
    [existingVehicles, defaultCategory],
  );

  return (
    <VehiclesPageContent
      existingVehicles={existingVehicles}
      vehiclesLoading={vehiclesLoading}
      totalCount={totalCount}
      page={page}
      pageSize={pageSize}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
      onSearchChange={handleSearchQueryChange}
      newRows={newRows}
      onNewRowsChange={handleNewRowsChange}
      onRowsParsed={handleRowsParsed}
      onDeleteExisting={handleDeleteExisting}
      onSaveEdit={handleSaveEdit}
      onSubmitNew={handleSubmitNew}
      onSubmit={() => submit(newRows)}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      progress={progress}
      bannerResult={bannerResult}
      onDismissBanner={() => setBannerResult(null)}
    />
  );
};

export default VehiclesPage;
